import { useCallback } from 'react';
import { NavigateFunction, useNavigate } from 'react-router-dom';
// @ts-ignore - lib/billing is plain JS
import { CHECKOUT_STEPS } from '../../lib/billing/config';
import { analytics } from 'lib/analytics';
import { useSubscriptionStatus } from '../../hooks/useSubscriptionStatus';
import { useBillingStore } from '../account-settings/subscriptions/checkout-v2/store/useBillingStore';
import { useCheckoutStore } from '../account-settings/subscriptions/checkout-v2/store/useCheckoutStore';
import { QUICK_PRODUCTS, QuickOpts } from './quickProducts';
import { QuickPreset, useQuickSubscribe } from './useQuickSubscribe';
import { onTourRoute, ROUTES_TOUR } from '../welcome-tour/tourRoutes';
import { useTour } from '../../contexts/TourContext';

// The checkout drawer mounts in MainLayout, which the tour route lacks: land
// on the dashboard with the drawer open, and remember the origin so closing
// it (paid or not) returns to the tour. No-op off the tour route.
export const leaveTourForCheckout = (navigate: NavigateFunction): void => {
  if (!onTourRoute()) return;
  useQuickSubscribe.getState().setReturnTo(ROUTES_TOUR.TOUR);
  navigate(ROUTES_TOUR.DASHBOARD);
};

/**
 * Billing owns changes to a running plan. From the tour it is the plans
 * step's route, so hand off through the tour and its bar stands on the page;
 * `openCheckout` opens the drawer on its first step, hydrated from the live
 * subscription.
 */
export const useToBilling = () => {
  const navigate = useNavigate();
  const { openTarget } = useTour();
  return useCallback(
    (openCheckout: boolean) => {
      if (!onTourRoute()) {
        navigate(ROUTES_TOUR.BILLING);
        return;
      }
      if (openCheckout) useCheckoutStore.getState().openDrawer();
      openTarget();
    },
    [navigate, openTarget]
  );
};

/**
 * From a surface that has already asked every question — the tour's ticket,
 * the lock panel — go straight to payment with that exact cart. No modal in
 * between re-asking what was just answered. The quick-subscribe modal stays
 * the fallback while prices are still loading (it waits for them), and past
 * due accounts are sent to Billing to settle first, as the modal does.
 */
export const useStageCheckout = () => {
  const navigate = useNavigate();
  const toBilling = useToBilling();
  const { isPaid, hasPastDue, hadPreviousSub: returning } = useSubscriptionStatus();
  const catalog = useBillingStore((s) => s.catalog);
  const overview = useBillingStore((s) => s.overview);

  return useCallback(
    (productKey: string, preset: QuickPreset | undefined, source: string) => {
      analytics.getStartedCtaClicked({ step: 'subscribe', plan: productKey, source, returning, cycle: preset?.cycle });
      if (hasPastDue) {
        // An overdue subscription can't be re-bought — staging the same plan
        // resolves to zero changes and errors at payment. Settle it instead.
        toBilling(false);
        return;
      }
      // Already subscribed. These carts are sized for a FIRST purchase, so
      // staging one over a live subscription resolves to a quantity change —
      // a subscriber with ten seats who pressed a "2 accounts" key would have
      // been dropped to two. Changes to a running plan belong in Billing.
      // Test for `true` explicitly: null means billing is still loading.
      if (isPaid === true) {
        toBilling(true);
        return;
      }
      const product = QUICK_PRODUCTS.find((p) => p.key === productKey);
      if (!catalog || !product) {
        useQuickSubscribe.getState().openQuickSubscribe(productKey, preset);
        return;
      }
      const opts: QuickOpts = {
        tier: preset?.tier === 'premium' ? 'premium' : 'standard',
        accounts: preset?.accounts ?? 2,
        cycle: preset?.cycle,
        futuresExtra: preset?.futuresExtra ?? 0,
        modes: preset?.mode ? { [productKey]: preset.mode } : undefined,
        analyzer: Boolean(preset?.analyzer),
        trialUsedProductIds: ((overview as any)?.trialUsedProductIds as string[]) || []
      };
      const store = useCheckoutStore.getState();
      // Hand the cart to CheckoutProvider as a one-shot patch instead of
      // writing the selection directly: hydration from active subscriptions
      // replaces the whole selection on drawer open, which would silently
      // wipe a directly-staged cart for anyone who already owns an add-on.
      // skipTrial: false explicitly — hydration from an existing subscription
      // sets it true, and merging on top of that turned a "$0 today" promise
      // into an immediate charge.
      store.setPendingQuickPatch({
        skipTrial: false,
        ...product.stage(catalog, opts),
        ...(opts.analyzer ? { analyzerEnabled: true } : null)
      });
      useQuickSubscribe.getState().markEntry('ticket');
      store.openDrawer(CHECKOUT_STEPS.payment);
      leaveTourForCheckout(navigate);
    },
    [navigate, toBilling, isPaid, hasPastDue, returning, catalog, overview]
  );
};
